import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useBrand } from "@/lib/brand.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Pill, EmptyState } from "@/components/ui-bits";
import { formatINR, fmtDate, statusTone } from "@/lib/format";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Inquiry", "Proposal", "Active", "On Hold", "Completed", "Closed"];

const empty = {
  name: "", brand_id: "", client_id: "", description: "", status: "Inquiry",
  start_date: "", end_date: "", value: 0, team: [], notes: "",
};

export default function Projects() {
  const { brands, brandQuery, activeBrandId } = useBrand();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get("/projects", { params: brandQuery }),
      api.get("/clients"),
    ]);
    setItems(a.data); setClients(b.data);
  }, [brandQuery]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...empty, brand_id: activeBrandId !== "all" ? activeBrandId : brands[0]?.id || "" });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...empty, ...p, client_id: p.client_id || "", start_date: p.start_date || "", end_date: p.end_date || "" });
    setOpen(true);
  };
  const submit = async () => {
    try {
      const payload = { ...form, value: Number(form.value || 0), client_id: form.client_id || null };
      if (editing) await api.put(`/projects/${editing.id}`, payload);
      else await api.post("/projects", payload);
      toast.success(editing ? "Project updated" : "Project created");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await api.delete(`/projects/${p.id}`); load();
  };

  return (
    <div data-testid="projects-page">
      <PageHeader
        eyebrow="Engagements"
        title="Projects"
        description="Active engagements across brands with timelines, value and team."
        actions={
          <Button data-testid="new-project-btn" onClick={openCreate} className="bg-bronze text-black hover:bg-[#d4af68]">
            <Plus className="w-4 h-4 mr-1.5" /> New project
          </Button>
        }
      />

      {items.length === 0 ? (
        <EmptyState title="No projects yet" />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Project</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Brand</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Client</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Timeline</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Value</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Status</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => {
                const brand = brands.find((b) => b.id === p.brand_id);
                const client = clients.find((c) => c.id === p.client_id);
                return (
                  <TableRow key={p.id} className="border-border hover:bg-[#f8f6f2]" data-testid={`project-row-${p.id}`}>
                    <TableCell>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[320px]">{p.description}</div>
                    </TableCell>
                    <TableCell>{brand ? <Pill>{brand.code}</Pill> : "—"}</TableCell>
                    <TableCell className="text-sm">{client?.company || client?.name || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(p.start_date)} → {fmtDate(p.end_date)}</TableCell>
                    <TableCell className="font-mono text-sm text-bronze">{formatINR(p.value)}</TableCell>
                    <TableCell><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-medium border ${statusTone(p.status)}`}>{p.status}</span></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(p)} className="text-muted-foreground hover:text-bronze" data-testid={`edit-project-${p.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => del(p)} className="text-muted-foreground hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="project-dialog" className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit project" : "New project"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Name</Label><Input data-testid="project-name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="bg-white" /></div>
            <div>
              <Label>Brand</Label>
              <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Brand" /></SelectTrigger>
                <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} className="bg-white" /></div>
            <div><Label>Start</Label><Input type="date" value={form.start_date} onChange={(e)=>setForm({...form, start_date:e.target.value})} className="bg-white" /></div>
            <div><Label>End</Label><Input type="date" value={form.end_date} onChange={(e)=>setForm({...form, end_date:e.target.value})} className="bg-white" /></div>
            <div><Label>Value (₹)</Label><Input type="number" value={form.value} onChange={(e)=>setForm({...form, value:e.target.value})} className="bg-white" /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} className="bg-white" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-project-btn" onClick={submit} className="bg-bronze text-black hover:bg-[#d4af68]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
